import React, { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { IoMdClose } from "react-icons/io";
import { Imagekit, ikSrc } from "../../Pages/CommonPages/ImageKit";
import useIsDesktop from "../../Hooks/useIsDesktop";

/**
 * BehindScenesRow
 *
 * Sourcing-trip video teaser shelf, fed from get_home_page
 * (behindTheScenes.videos). Tapping a card plays the clip in a lightbox
 * (same as the full page); "View all" → /behind-the-scene.
 *
 * @param {Array}  videos - [{ id, title, posterImage, category, region, duration, videoUrl }] — ONE PAGE
 * @param {number} total  - totalElements from the paginated envelope
 */
export default function BehindScenesRow({ videos = [], total }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const [active, setActive] = useState(null);

  if (!videos?.length) return null;

  // Cap visible cards per breakpoint (mobile 4 / desktop 7). `videos` is ONE PAGE
  // of get_home_page (pageSize hardcoded to 4), so the old `videos.length > cap`
  // test could never be true — "is there more?" is only answerable from the
  // envelope's totalElements. Compared against the cards on screen rather than
  // `cap` so nothing is stranded while the page stays at 4.
  // See CraftClusterRow for the full reasoning.
  const cap = isDesktop ? 7 : 4;
  const visibleVideos = videos.slice(0, cap);
  const totalCount = total ?? videos.length; // fall back if envelope is absent
  const showViewAll = totalCount > visibleVideos.length;

  return (
    <>
      <section className="pt-[8px] pb-[14px]">
        <div className="px-4 pb-[11px]">
          <h2 className="text-[17px] font-[500] text-ink-900 tracking-[-0.01em]">
            <Trans
              i18nKey="behindScenes.heading"
              components={[<span className="text-coral-ink" />]}
            />
          </h2>
          <div className="text-[11px] text-ink-500 mt-[3px]">
            {t("behindScenes.sub")}
          </div>
        </div>

        <div className="flex gap-[11px] overflow-x-auto no-scrollbar pt-[2px] px-4 pb-[8px]">
          {visibleVideos.map((v) => (
            <button
              key={v.id}
              type="button"
              onClick={() => setActive(v)}
              className="flex-none w-[228px] text-left"
            >
              <div className="relative w-full aspect-[16/9] rounded-lg overflow-hidden bg-ink-900">
                <Imagekit
                  IMGName={v.posterImage}
                  className="w-full h-full object-cover object-top opacity-[0.82]"
                  eager
                />
                <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[42px] h-[42px] rounded-full bg-white/90 grid place-items-center shadow-[0_4px_14px_rgba(0,0,0,0.3)]">
                  <svg
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="w-[16px] h-[16px] text-ink-900 ml-[2px]"
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </span>
                <span className="absolute bottom-[7px] right-[7px] text-[9.5px] font-[500] text-white bg-black/[0.62] px-[6px] py-[2px] rounded-[5px]">
                  {v.duration}
                </span>
              </div>
              {v.category && (
                <div className="text-[8px] font-[500] tracking-[0.04em] uppercase text-coral-ink mt-[8px]">
                  {v.category}
                </div>
              )}
              <div className="text-[11.5px] font-[500] text-ink-700 mt-[2px] leading-[1.35] line-clamp-2 min-h-[32px]">
                {v.region ? `${v.title} · ${v.region}` : v.title}
              </div>
            </button>
          ))}

          {/* View all — only when videos overflow the visible run.
              Footprint must match a video POSTER, not the whole card: same
              w-[228px] and aspect-[16/9] as the poster div above. A video card is
              a poster plus untinted caption text below it, so `self-start` is
              required — the row is a flex container (align-items: stretch), which
              would otherwise pull this tile down past the poster to the full card
              height and leave the tint overhanging the captions. */}
          {showViewAll && (
            <button
              type="button"
              onClick={() => navigate("/behind-the-scene")}
              className="flex-none w-[228px] self-start"
            >
              <div className="w-full aspect-[16/9] rounded-lg bg-[#F4F5FF] flex items-center justify-center">
                <span className="text-[12px] font-[500] text-s-primary text-center px-[12px]">
                  View all →
                </span>
              </div>
            </button>
          )}
        </div>
      </section>

      {/* Video lightbox */}
      {active && (
        <div
          className="fixed inset-0 z-[1000] flex flex-col items-center justify-center bg-[rgba(15,16,24,0.93)] p-[16px]"
          onClick={() => setActive(null)}
        >
          <button
            onClick={() => setActive(null)}
            aria-label="Close"
            className="absolute top-[12px] right-[12px] w-[36px] h-[36px] rounded-full bg-white/[0.16] grid place-items-center text-white cursor-pointer z-[3]"
          >
            <IoMdClose className="w-[18px] h-[18px]" />
          </button>

          <div
            className="w-full max-w-[560px]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative w-full aspect-[16/9] rounded-[12px] overflow-hidden bg-black">
              <video
                src={active.videoUrl}
                poster={ikSrc(active.posterImage, [{ width: 800 }])}
                controls
                autoPlay
                className="w-full h-full object-contain"
              />
            </div>
            <div className="text-white mt-[11px]">
              <div className="text-[14px] font-[500] leading-[1.25]">
                {active.title}
              </div>
              <div className="text-[10.5px] text-white/70 mt-[2px]">
                {[active.category, active.region].filter(Boolean).join(" · ")}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
